import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./api";
import { Alert, Badge, Button, Card, Modal, useToast } from "./ui";
import { dateTime } from "./format";

const MAX_MB = 10;

/** Photos & documents attached to a record. Files are private and served only through the API. */
export function FilesPanel({ ownerType, ownerId, title = "Photos & documents", category = "document" }: { ownerType: string; ownerId: string; title?: string; category?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["files", ownerType, ownerId];
  const q = useQuery({ queryKey: key, queryFn: () => api.get(`/files?ownerType=${ownerType}&ownerId=${ownerId}`) });
  const [failed, setFailed] = useState<string | null>(null);
  const cam = useRef<HTMLInputElement>(null);
  const pick = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files) {
        if (f.size > MAX_MB * 1024 * 1024) throw new ApiError(413, "too_large", `${f.name} is larger than ${MAX_MB} MB.`);
        const fd = new FormData();
        fd.append("file", f); fd.append("ownerType", ownerType); fd.append("ownerId", ownerId); fd.append("category", category);
        await api.post("/files", fd);
      }
      return files.length;
    },
    onSuccess: (n) => { setFailed(null); qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["job"] }); toast("success", `${n} file${n > 1 ? "s" : ""} uploaded`); },
    onError: (e) => { setFailed((e as Error).message); qc.invalidateQueries({ queryKey: key }); },
  });
  const remove = useMutation({ mutationFn: (id: string) => api.del(`/files/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: key }), onError: (e) => toast("error", (e as Error).message) });
  const onFiles = (list: FileList | null) => { if (list?.length) upload.mutate([...list]); if (cam.current) cam.current.value = ""; if (pick.current) pick.current.value = ""; };
  const files: any[] = q.data?.files ?? [];
  return (
    <Card title={title} actions={<>
      <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={(e) => onFiles(e.target.files)} />
      <input ref={pick} type="file" accept="image/jpeg,image/png,image/webp,image/heic,application/pdf" multiple hidden onChange={(e) => onFiles(e.target.files)} />
      <Button size="sm" onClick={() => cam.current?.click()} loading={upload.isPending}>📷 Take photo</Button>
      <Button size="sm" onClick={() => pick.current?.click()} disabled={upload.isPending}>Choose file</Button>
    </>}>
      {failed && <div style={{ marginBottom: 8 }}><Alert kind="error" title="Upload failed — not saved">{failed}</Alert></div>}
      {q.isError ? <Alert kind="error">{(q.error as Error).message}</Alert> : !files.length ? <span className="muted small">None yet. Photos, PDFs (max {MAX_MB} MB each).</span> : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 8 }}>
          {files.map((f) => (
            <figure key={f.id} style={{ margin: 0, border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden", background: "var(--grey-50)" }}>
              <a href={`/api/files/${f.id}/content`} target="_blank" rel="noopener">
                {f.mime.startsWith("image/") && f.mime !== "image/heic" ? <img src={`/api/files/${f.id}/content`} alt={f.filename} loading="lazy" style={{ width: "100%", aspectRatio: "4/3", objectFit: "cover", display: "block" }} />
                  : <div style={{ aspectRatio: "4/3", display: "grid", placeItems: "center", fontWeight: 700 }}>{f.mime === "application/pdf" ? "PDF" : "HEIC"}</div>}
              </a>
              <figcaption className="small" style={{ padding: 6 }}>
                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.filename}>{f.filename}</div>
                <div className="muted">{dateTime(f.created_at)}</div>
                <Button size="sm" variant="ghost" onClick={() => remove.mutate(f.id)}>Remove</Button>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </Card>
  );
}

/** Create a secure customer link and show a ready-to-copy message. Nothing is sent automatically. */
export function ShareLinkButton({ purpose, quoteId, jobId, customerName, label }: { purpose: "quote_approval" | "job_status"; quoteId?: string; jobId?: string; customerName?: string; label: string }) {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState<{ url: string; expiresAt: string } | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const listKey = ["links", quoteId ?? jobId];
  const links = useQuery({ queryKey: listKey, queryFn: () => api.get(`/links?${quoteId ? `quoteId=${quoteId}` : `jobId=${jobId}`}`), enabled: open });
  const create = useMutation({ mutationFn: () => api.post("/links", { purpose, quoteId, jobId }), onSuccess: (r: any) => { setLink(r); qc.invalidateQueries({ queryKey: listKey }); qc.invalidateQueries({ queryKey: ["quote"] }); } });
  const revoke = useMutation({ mutationFn: (id: string) => api.post(`/links/${id}/revoke`), onSuccess: () => qc.invalidateQueries({ queryKey: listKey }) });
  const first = customerName?.split(" ")[0] ?? "";
  const message = link ? (purpose === "quote_approval"
    ? `Hi ${first}, here is your quote from Wali Auto Fix. You can review and approve it here: ${link.url}`
    : `Hi ${first}, you can check the progress of your car here: ${link.url}`) : "";
  const copy = async (t: string) => { try { await navigator.clipboard.writeText(t); toast("success", "Copied"); } catch { toast("error", "Copy failed — select the text and copy it manually"); } };
  return (
    <>
      <Button onClick={() => setOpen(true)}>{label}</Button>
      {open && (
        <Modal title={label} onClose={() => { setOpen(false); setLink(null); }}>
          {!link ? (
            <>
              <p>{purpose === "quote_approval" ? "Creates a private link where the customer can view this quote and approve or decline the current revision. The quote is marked as sent." : "Creates a private link showing only the job status and vehicle — no notes or prices."}</p>
              <p className="small muted">Links expire after 14 days and can be revoked. Nothing is sent — you copy the message yourself.</p>
              <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>Create link</Button>
              {create.error && <div style={{ marginTop: 8 }}><Alert kind="error">{(create.error as Error).message}</Alert></div>}
            </>
          ) : (
            <div className="stack">
              <Alert kind="success" title="Link created">Copy it now — for security it won't be shown again. Expires {dateTime(link.expiresAt)}.</Alert>
              <textarea className="input" readOnly value={message} rows={4} onFocus={(e) => e.target.select()} />
              <div className="row"><Button variant="primary" onClick={() => copy(message)}>Copy message</Button><Button onClick={() => copy(link.url)}>Copy link only</Button></div>
            </div>
          )}
          {(links.data?.links ?? []).length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h3>Existing links</h3>
              {links.data.links.map((l: any) => (
                <div key={l.id} className="row small" style={{ margin: "6px 0" }}>
                  {l.revoked_at ? <Badge tone="red">revoked</Badge> : l.expires_at < new Date().toISOString() ? <Badge>expired</Badge> : <Badge tone="green">active</Badge>}
                  <span className="muted">created {dateTime(l.created_at)}{l.last_viewed_at ? ` · last opened ${dateTime(l.last_viewed_at)}` : " · not opened yet"}</span>
                  {!l.revoked_at && <Button size="sm" variant="ghost" onClick={() => revoke.mutate(l.id)}>Revoke</Button>}
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
